/**
 * How an added instrument's check shows on its grid row, the way the review page shows an issue:
 * a row waiting on a fix takes the warning background, an instrument that cannot be held the
 * soft-deleted look and no selection checkbox.
 */
import type { RowClassParams, RowClassRules, RowSelectionOptions } from "ag-grid-community";
import type { Instrument } from "../../const";

const WARNING_ROW = "bg-[var(--global-palette-warning-100)]";
const GREYED_ROW = "bg-[var(--Table-highlightedRowBackgroundColor)]";

function statusOf(params: RowClassParams<Instrument>) {
	return params.data?.deleted ? undefined : params.data?.newInstrumentCheck?.status;
}

const NEW_INSTRUMENT_ROW_CLASSES: Record<string, (params: RowClassParams<Instrument>) => boolean> = {
	[WARNING_ROW]: (params) => statusOf(params) === "fixable" || statusOf(params) === "failed",
	[GREYED_ROW]: (params) => statusOf(params) === "unfixable",
};

/** `rules` with the check's row classes added — a class both name applies when either rule does. */
export function withNewInstrumentRowClasses(rules: RowClassRules<Instrument>): RowClassRules<Instrument> {
	const merged: RowClassRules<Instrument> = { ...rules };
	for (const [className, applies] of Object.entries(NEW_INSTRUMENT_ROW_CLASSES)) {
		const existing = rules[className];
		merged[className] =
			typeof existing === "function" ? (params) => Boolean(existing(params)) || applies(params) : applies;
	}
	return merged;
}

/** `guard` with an instrument that cannot be held made unselectable, so no bulk action reaches it. */
export function withUnfixableUnselectable<T extends Partial<RowSelectionOptions<Instrument>>>(guard: T): T {
	const { isRowSelectable } = guard;
	return {
		...guard,
		isRowSelectable: (node) =>
			node.data?.newInstrumentCheck?.status !== "unfixable" && (isRowSelectable ? isRowSelectable(node) : true),
	};
}
